import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import bgimage from './assets/images/login.jpg';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { loadGoogleTag, trackSignupConversion } from './utils/googleTag';
import './Login.css';
import './Register.css';

function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    organizationName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    orgRole: '',
    hp: '', // honeypot — hidden from humans; bots fill it
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Load the Google tag on this public page only, so a conversion can be recorded on success.
  useEffect(() => { loadGoogleTag(); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      setError(t('Register.passwordsDoNotMatch'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('Register.passwordMinLength'));
      return;
    }
    if (form.password.length > 72) {
      setError('Password is too long (maximum 72 characters).');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/register`, {
        organizationName: form.organizationName,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        password: form.password,
        orgRole: form.orgRole || null,
        website: form.hp,
      });

      setSuccess(true);
      // Count only standard customer sign-ups; reseller / CPA partner registrations are not leads.
      if (!form.orgRole) trackSignupConversion();
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || t('Register.registrationFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-page" style={{ backgroundImage: `url(${bgimage})` }}>
        <div className="login-card">
          <div className="success-box">
            <div className="success-icon">✓</div>
            <h2>{t('Register.accountCreatedHeading')}</h2>
            <p>{t('Register.accountCreatedBody')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ backgroundImage: `url(${bgimage})` }}>
      <div className="login-card register-card">
        <form className="login-form" onSubmit={handleSubmit}>
          {/* Honeypot: invisible and unreachable for people (off-screen, no tab stop, hidden from assistive
              tech); bots that auto-fill every field give themselves away. Named oddly so browsers/password
              managers don't autofill it. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="hp_ref">Leave this field empty</label>
            <input type="text" id="hp_ref" name="hp" value={form.hp} onChange={handleChange}
                   tabIndex={-1} autoComplete="off" />
          </div>
          <h2>{t('Register.startFreeTrial')}</h2>
          <p className="register-subtitle">{t('Register.subtitle')}</p>

          {error && <p className="error-message">{error}</p>}

          <label>{t('Register.organizationNameLabel')}</label>
          <input
            type="text"
            name="organizationName"
            maxLength={100}
            autoComplete="organization"
            value={form.organizationName}
            onChange={handleChange}
            required
            placeholder={t('Register.organizationNamePlaceholder')}
          />

          <div className="name-row">
            <div>
              <label>{t('Register.firstNameLabel')}</label>
              <input
                type="text"
                name="firstName"
            maxLength={60}
            autoComplete="given-name"
                value={form.firstName}
                onChange={handleChange}
                required
                placeholder={t('Register.firstNamePlaceholder')}
              />
            </div>
            <div>
              <label>{t('Register.lastNameLabel')}</label>
              <input
                type="text"
                name="lastName"
            maxLength={60}
            autoComplete="family-name"
                value={form.lastName}
                onChange={handleChange}
                required
                placeholder={t('Register.lastNamePlaceholder')}
              />
            </div>
          </div>

          <label>{t('Register.workEmailLabel')}</label>
          <input
            type="email"
            name="email"
            maxLength={254}
            autoComplete="email"
            value={form.email}
            onChange={handleChange}
            required
            placeholder={t('Register.workEmailPlaceholder')}
          />

          <label>{t('Register.phoneNumberLabel')}</label>
          <input
            type="tel"
            name="phoneNumber"
            maxLength={20}
            autoComplete="tel"
            value={form.phoneNumber}
            onChange={handleChange}
            placeholder="+1 555 000 0000"
          />

          <label>{t('Register.iAmALabel')}</label>
          <select name="orgRole" value={form.orgRole} onChange={handleChange} className="role-select">
            <option value="">{t('Register.roleSecurityCompliance')}</option>
            <option value="CISO_RESELLER">{t('Register.roleCisoReseller')}</option>
            <option value="CPA_ADMIN">{t('Register.roleCpaAuditor')}</option>
          </select>

          <label>{t('Register.passwordLabel')}</label>
          <input
            type="password"
            name="password"
            maxLength={72}
            minLength={8}
            autoComplete="new-password"
            value={form.password}
            onChange={handleChange}
            required
            placeholder={t('Register.passwordPlaceholder')}
          />

          <label>{t('Register.confirmPasswordLabel')}</label>
          <input
            type="password"
            name="confirmPassword"
            maxLength={72}
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={handleChange}
            required
            placeholder={t('Register.confirmPasswordPlaceholder')}
          />

          <button type="submit" disabled={loading}>
            {loading ? t('Register.creatingAccount') : t('Register.createFreeAccount')}
          </button>

          <p className="login-link">
            {t('Register.alreadyHaveAccount')} <Link to="/login">{t('Register.signIn')}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Register;
